import { useEffect, useRef, useCallback } from "react";
import { loadModules } from "esri-loader";
import { GeographicDistributionItem } from "@/types/projects";
import {
  MAP_CONFIG,
  CIRCLE_CONFIG,
  getCityCoordinates,
  calculateCircleRadius,
  getProjectLevel,
  createCirclePoints,
} from "./constants";
import { EsriMapView, MapClickEvent, HitTestResult } from "./types";

interface TooltipData {
  projectCount: number;
  levelLabel: string;
  x: number;
  y: number;
}

/**
 * Creates a hover handler for map interactions
 * Updates React state to show custom tooltip when hovering over project circles
 */
const createMapHoverHandler =
  (
    view: EsriMapView,
    container: HTMLDivElement,
    setTooltipData: React.Dispatch<React.SetStateAction<TooltipData | null>>
  ) =>
  (event: MapClickEvent) => {
    event.stopPropagation?.();
    view.hitTest(event).then((response: HitTestResult) => {
      if (response.results.length > 0) {
        const graphic = response.results[0].graphic;
        if (
          graphic?.attributes &&
          graphic.attributes.isProjectCircle === true
        ) {
          const rect = container.getBoundingClientRect();
          setTooltipData({
            projectCount: graphic.attributes.projectCount,
            levelLabel: graphic.attributes.levelLabel,
            x: rect.left + event.x,
            y: rect.top + event.y,
          });
        } else {
          setTooltipData(null);
        }
      } else {
        setTooltipData(null);
      }
    });
  };

/**
 * Custom hook for initializing and managing the Esri map
 * @param mapRef - Reference to the DOM element that will contain the map
 * @param filteredData - Array of geographic distribution items to display on the map
 * @param loading - Loading state to prevent premature initialization
 * @param setTooltipData - Callback to update tooltip state
 */
export const useMapInitialization = (
  mapRef: React.RefObject<HTMLDivElement>,
  filteredData: GeographicDistributionItem[],
  loading: boolean,
  setTooltipData: React.Dispatch<React.SetStateAction<TooltipData | null>>
) => {
  const viewRef = useRef<EsriMapView | null>(null);

  // Memoize initialization logic to satisfy exhaustive-deps
  const initializeMap = useCallback(
    async (container: HTMLDivElement) => {
      try {
        const [EsriMap, MapView, Graphic, SimpleFillSymbol, Polygon] =
          await loadModules(
            [
              "esri/Map",
              "esri/views/MapView",
              "esri/Graphic",
              "esri/symbols/SimpleFillSymbol",
              "esri/geometry/Polygon",
            ],
            { css: true }
          );

        if (viewRef.current) {
          viewRef.current.destroy();
          viewRef.current = null;
        }

        const esriMap = new EsriMap({
          basemap: MAP_CONFIG.basemap,
        });

        const view = new MapView({
          container,
          map: esriMap,
          center: MAP_CONFIG.center,
          zoom: MAP_CONFIG.zoom,
        });

        viewRef.current = view;
        await view.when();

        view.on(
          "pointer-move",
          createMapHoverHandler(view, container, setTooltipData)
        );

        const maxProjects = Math.max(
          ...filteredData.map((item) => item.projectCount)
        );

        for (const item of filteredData) {
          const coords = getCityCoordinates(item);
          const [lat, lon] = coords;
          const radius = calculateCircleRadius(item.projectCount, maxProjects);
          const level = getProjectLevel(item.projectCount);

          const circle = new Polygon({
            rings: createCirclePoints(lon, lat, radius),
          });

          const symbol = new SimpleFillSymbol({
            color: level.color,
            outline: {
              color: [level.color[0], level.color[1], level.color[2], 0.9],
              width: CIRCLE_CONFIG.outlineWidth,
            },
          });

          const graphic = new Graphic({
            geometry: circle,
            symbol: symbol,
            attributes: {
              city: item.city,
              projectCount: item.projectCount,
              levelLabel: level.label,
              lon,
              lat,
              isProjectCircle: true,
            },
          });

          view.graphics.add(graphic);
        }
      } catch (err) {
        console.error("Error initializing map:", err);
      }
    },
    [filteredData, setTooltipData]
  );

  useEffect(() => {
    if (!mapRef.current || filteredData.length === 0 || loading) {
      return;
    }

    let isMounted = true;
    const container = mapRef.current;

    const init = async () => {
      if (isMounted) {
        await initializeMap(container);
      }
    };

    init();

    return () => {
      isMounted = false;
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, [filteredData, loading, initializeMap]);
};
